import React, { useState, useEffect, useCallback } from 'react'
import moment from 'moment'
import { useLocation, useHistory } from 'react-router-dom'
import queryString from 'query-string'
import axios from 'axios'

import { requestCreator } from '../../utilities/requests'
import FacilitiesKlassDaysIndex from './FacilitiesKlassDaysIndex'
import useFacility from '../../utilities/hooks/useFacility'

const { get: getKlasses, cancel: cancelKlassesRequest } = requestCreator()

function FacilitiesKlassDaysIndexFeature() {
  const { push: historyPush, replace: redirect } = useHistory()
  const location = useLocation()
  const { facility, isLoading: isFacilityLoading } = useFacility()
  const [klasses, setKlasses] = useState([])
  const [isLoadingKlasses, setIsLoadingKlasses] = useState(true)
  const [loadedRange, setLoadedRange] = useState({ start: null, end: null })

  const { month } = queryString.parse(location.search)
  const selectedMonth = month
    ? moment().year(month.split('-')[0]).month(month.split('-')[1]).subtract(1, 'month')
    : moment()

  const fetchKlassesForRange = useCallback(
    (startDate, endDate) => {
      return getKlasses(`/api/facilities/${facility.id}/klasses`, {
        params: {
          context: 'calendar',
          start_date: startDate.format('YYYY-MM-DD'),
          end_date: endDate.format('YYYY-MM-DD')
        }
      })
    },
    [facility.id]
  )

  useEffect(() => {
    if (!facility.id) return

    const startDate = moment().subtract(2, 'months').startOf('month')
    const endDate = moment().add(2, 'months').endOf('month')

    setIsLoadingKlasses(true)
    fetchKlassesForRange(startDate, endDate)
      .then((newKlasses) => {
        setKlasses(newKlasses)
        setLoadedRange({ start: startDate.clone(), end: endDate.clone() })
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          return
        } else if (error.response?.status === 401) {
          redirect('/login')
        } else {
          throw error
        }
      })
      .finally(() => setIsLoadingKlasses(false))

    return cancelKlassesRequest
  }, [facility.id, redirect, fetchKlassesForRange])

  useEffect(() => {
    if (!loadedRange.start || !selectedMonth || !facility.id) return

    const monthStart = selectedMonth.clone().startOf('month')
    const monthEnd = selectedMonth.clone().endOf('month')

    if (monthStart.isBefore(loadedRange.start) || monthEnd.isAfter(loadedRange.end)) {
      const newStart = monthStart.isBefore(loadedRange.start)
        ? monthStart.clone().subtract(1, 'month')
        : loadedRange.start.clone()
      const newEnd = monthEnd.isAfter(loadedRange.end) ? monthEnd.clone().add(1, 'month') : loadedRange.end.clone()

      setIsLoadingKlasses(true)
      fetchKlassesForRange(newStart, newEnd)
        .then((newKlasses) => {
          setKlasses(newKlasses)
          setLoadedRange({ start: newStart.clone(), end: newEnd.clone() })
        })
        .catch((error) => {
          if (axios.isCancel(error)) {
            return
          } else if (error.response?.status === 401) {
            redirect('/login')
          } else {
            throw error
          }
        })
        .finally(() => setIsLoadingKlasses(false))
    }
  }, [selectedMonth, loadedRange, facility.id, redirect, fetchKlassesForRange])

  useEffect(() => {
    if (month || isFacilityLoading || klasses.length === 0) return

    navigateToMonth(moment())
  }, [klasses, isFacilityLoading, month])

  const navigateToMonth = useCallback(
    (newMonth) => {
      const { month: locationMonth } = queryString.parse(location.search)
      if (locationMonth && newMonth.isSame(selectedMonth, 'month')) return

      const search = `?month=${newMonth.format('YYYY-MM')}`
      historyPush({ search })
    },
    [location.search, historyPush, selectedMonth]
  )

  return (
    <FacilitiesKlassDaysIndex
      isLoadingKlasses={isLoadingKlasses}
      facility={facility}
      klasses={klasses}
      selectedMonth={selectedMonth}
      onMonthChange={navigateToMonth}
    />
  )
}

export default FacilitiesKlassDaysIndexFeature
